import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { APP_GUIDES } from '../data/guides';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import LandingFigure, { ScreenshotGallery } from '../components/LandingFigure';
import { SITE_LAST_UPDATED } from '../data/features';
import { longDate } from '../lib/siteDates';
import { GOOGLE_APP_INTEGRATIONS } from '../lib/constants';
import {
  ArrowRight, Check, Plug, Calendar, Mail, Webhook, Bot, ChevronRight,
} from 'lucide-react';

// The words of these pages live in public/locales/<lang>/integrations.json: the overview
// under overview (its cards under overview.categories / overview.items), each integration
// under its key, with one alt and caption per screenshot here, in the same order.
const CATEGORIES = [
  ...(GOOGLE_APP_INTEGRATIONS ? [
    { key: 'calendar', items: [{ key: 'google_calendar', icon: Calendar, path: '/integrations/google-calendar' }] },
    { key: 'email', items: [{ key: 'gmail', icon: Mail, path: '/integrations/gmail' }] },
  ] : []),
  { key: 'automation', items: [{ key: 'webhooks', icon: Webhook, path: '/integrations/webhooks' }] },
  { key: 'mcp', items: [{ key: 'mcp', icon: Bot, path: '/integrations/mcp' }] },
];

const INTEGRATIONS = {
  'google-calendar': { key: 'google_calendar', icon: Calendar, screenshots: ['/screenshots/calendar/agenda.png', '/screenshots/calendar/planner.png'] },
  gmail: { key: 'gmail', icon: Mail, screenshots: ['/screenshots/gmail-integration/main.png'] },
  webhooks: { key: 'webhooks', icon: Webhook, screenshots: ['/screenshots/webhooks/main.png'] },
};
if (!GOOGLE_APP_INTEGRATIONS) {
  delete INTEGRATIONS['google-calendar'];
  delete INTEGRATIONS.gmail;
}

const asList = (v) => (Array.isArray(v) ? v : []);
const textOf = (t, key) => {
  const c = t(`integrations:${key}`, { returnObjects: true });
  return c && typeof c === 'object' ? c : {};
};

// Services bots can use through MCP; each has its own page (data/guides.js).
const BotApps = () => {
  const { t } = useTranslation('landing');
  return (
    <section className="max-w-7xl mx-auto px-4 py-8">
      <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">{t('integrations_page.bot_apps.headline')}</h2>
      <p className="text-gray-400 mb-6 max-w-3xl">{t('integrations_page.bot_apps.body')}</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {APP_GUIDES.map((g) => (
          <Link key={g.path} to={g.path} className="flex items-start gap-4 p-5 bg-white/5 rounded-2xl border border-gray-800 hover:border-amber-500/30 transition-colors group">
            <span className="w-10 h-10 rounded-lg bg-white p-1.5 flex items-center justify-center shrink-0">
              <img src={g.logo} alt="" className="w-full h-full object-contain" loading="lazy" />
            </span>
            <span>
              <span className="block text-white font-semibold group-hover:text-amber-400 transition-colors">{t(`guide_links.${g.path.slice(1)}.name`)}</span>
              <span className="block text-gray-400 text-sm mt-1">{t(`guide_links.${g.path.slice(1)}.desc`)}</span>
            </span>
          </Link>
        ))}
      </div>
      <Link to="/ai-bots/connected-apps" className="text-amber-400 hover:text-amber-300 font-medium inline-flex items-center gap-2 mt-6">
        {t('integrations_page.bot_apps.more')}
        <ArrowRight className="w-4 h-4" />
      </Link>
    </section>
  );
};

const IntegrationsPage = () => {
  const { t, i18n } = useTranslation(['landing', 'integrations']);
  const { integrationId } = useParams();

  // Overview page
  if (!integrationId) {
    const data = textOf(t, 'overview');
    return (
      <div className="min-h-screen bg-[#0a0a0b]">
        <SEO
          title={data.seo_title}
          description={data.seo_description}
          keywords={data.keywords}
          canonical="/integrations"
          breadcrumbs={[
            { name: t('pricing.breadcrumb_home'), url: '/' },
            { name: t('nav.section.integrations') },
          ]}
        />
        <Navbar />

        <main className="pt-12 pb-24">
          <section className="max-w-7xl mx-auto px-4 pt-12 pb-12 sm:pt-16 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-400 mb-6 text-xs font-medium">
              <Plug className="w-3.5 h-3.5" />
              <span>{t('nav.section.integrations')}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-5 leading-[1.05]">{data.title}</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">{data.subtitle}</p>
            <p className="text-base text-gray-400 leading-relaxed max-w-3xl mx-auto">{data.summary}</p>
          </section>

          {/* Flatten every category's items into a single responsive grid. Each
              category currently holds exactly one integration, so rendering one
              3-column grid per category left each card alone on its row with
              ~2/3 of the content width empty on the right (BG_59). A single
              2-column grid spreads the cards evenly across the full width and
              keeps the layout balanced; the category name moves to a label on
              the card so that context is preserved. */}
          <BotApps />

          <section className="max-w-7xl mx-auto px-4 py-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">{t('integrations_page.app_headline')}</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {CATEGORIES.flatMap((category) =>
                category.items.map((item) => {
                  const ItemIcon = item.icon;
                  return (
                    <Link
                      key={item.key}
                      to={item.path}
                      className="p-6 bg-white/5 rounded-2xl border border-gray-800 hover:border-amber-500/30 transition-colors group"
                    >
                      <div className="text-xs uppercase tracking-wider text-amber-400/80 font-semibold mb-3">{t(`integrations:overview.categories.${category.key}`)}</div>
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                          <ItemIcon className="w-5 h-5 text-amber-400" />
                        </div>
                        <h3 className="text-white font-semibold group-hover:text-amber-400 transition-colors">{t(`integrations:overview.items.${item.key}.name`)}</h3>
                      </div>
                      <p className="text-gray-400 text-sm">{t(`integrations:overview.items.${item.key}.desc`)}</p>
                    </Link>
                  );
                })
              )}
            </div>
          </section>

          <section className="max-w-4xl mx-auto px-4 py-16">
            <div className="bg-gradient-to-br from-amber-900/20 to-orange-900/20 rounded-2xl border border-gray-800 p-8 text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">{t('integrations_page.webhook_box.headline')}</h2>
              <p className="text-gray-400 mb-6 max-w-xl mx-auto">
                {t('integrations_page.webhook_box.body')}
              </p>
              <Link to="/integrations/webhooks" className="bg-amber-500 text-black px-6 py-3 rounded-lg font-semibold hover:bg-amber-400 transition-colors inline-flex items-center gap-2">
                {t('integrations_page.webhook_box.cta')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </section>

          <section className="max-w-4xl mx-auto px-4 py-8 text-center">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">{t('integrations_page.custom.headline')}</h2>
            <p className="text-gray-400 mb-6">{t('integrations_page.custom.body')}</p>
            <Link to="/developers" className="text-amber-400 hover:text-amber-300 font-medium inline-flex items-center gap-2">
              {t('integrations_page.custom.cta')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </section>
        </main>

        <Footer />
      </div>
    );
  }

  // Individual integration page
  const config = INTEGRATIONS[integrationId];
  if (!config) {
    return (
      <div className="min-h-screen bg-[#0a0a0b]">
        <Navbar />
        <main className="pt-20 pb-24 text-center">
          <h1 className="text-4xl font-bold text-white mb-4">{t('integrations_page.not_found.headline')}</h1>
          <Link to="/integrations" className="text-amber-400 hover:text-amber-300">{t('integrations_page.not_found.link')}</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const text = textOf(t, config.key);
  const shots = asList(text.screenshots);
  const integration = {
    ...text,
    features: asList(text.features),
    faq: asList(text.faq),
    screenshots: config.screenshots.map((src, i) => ({ src, ...shots[i] })),
  };
  const IntIcon = config.icon;
  const heroShot = integration.screenshots[0];

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <SEO
        title={integration.seo_title || integration.name}
        description={integration.seo_description}
        keywords={integration.keywords}
        canonical={`/integrations/${integrationId}`}
        image={heroShot?.src}
        faqItems={integration.faq}
        breadcrumbs={[
          { name: t('pricing.breadcrumb_home'), url: '/' },
          { name: t('nav.section.integrations'), url: '/integrations' },
          { name: integration.name },
        ]}
      />
      <Navbar />

      <main className="pt-12 pb-24">
        <ScreenshotGallery items={integration.screenshots || []}>
        {/* Hero */}
        <section className="max-w-7xl mx-auto px-4 pt-12 pb-12 sm:pt-16">
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8">
            <Link to="/" className="hover:text-white transition-colors">{t('pricing.breadcrumb_home')}</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to="/integrations" className="hover:text-white transition-colors">{t('nav.section.integrations')}</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-gray-300">{integration.name}</span>
          </nav>

          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-400 mb-6 text-xs font-medium">
                <IntIcon className="w-3.5 h-3.5" />
                <span>{t('integrations_page.badge')}</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-5 leading-[1.05]">
                {integration.name}
              </h1>
              <p className="text-xl text-gray-300 mb-5">{integration.summary}</p>
              <p className="text-base text-gray-400 leading-relaxed mb-8 max-w-2xl">{integration.description}</p>
              <div className="flex flex-wrap gap-3 mb-8">
                <Link to="/register" className="bg-amber-500 text-black px-6 py-3 rounded-lg font-semibold hover:bg-amber-400 transition-colors inline-flex items-center gap-2">
                  {t('integrations_page.cta_start_free')}
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/integrations" className="border border-gray-700 text-white px-6 py-3 rounded-lg font-medium hover:bg-white/5 transition-colors">
                  {t('integrations_page.cta_all')}
                </Link>
              </div>
              {/* No star rating: there is no review source behind one, and fabricated
                  ratings are banned by Annex I of Directive 2005/29/EC. */}
              <div className="flex items-center gap-6 text-sm text-gray-500">
                <span>{t('integrations_page.last_updated')} {longDate(SITE_LAST_UPDATED, i18n.resolvedLanguage || 'en')}</span>
              </div>
            </div>

            {heroShot && (
              <div className="lg:col-span-6">
                <LandingFigure
                  src={heroShot.src}
                  alt={heroShot.alt}
                  caption={heroShot.caption}
                  eager
                  frame="hero"
                />
              </div>
            )}
          </div>
        </section>

        {/* Features list */}
        <section className="max-w-4xl mx-auto px-4 py-16">
          <div className="mb-10">
            <div className="text-xs uppercase tracking-wider text-amber-400 font-semibold mb-2">{t('integrations_page.capabilities_label')}</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white">{t('integrations_page.capabilities_headline')}</h2>
          </div>
          <ul className="grid sm:grid-cols-2 gap-3">
            {integration.features.map((feature, i) => (
              <li key={i} className="flex items-start gap-3 p-4 bg-white/5 rounded-xl border border-gray-800">
                <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-4 h-4 text-amber-400" />
                </div>
                <span className="text-gray-300 text-sm leading-relaxed">{feature}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Additional screenshots */}
        {integration.screenshots && integration.screenshots.length > 1 && (() => {
          const extras = integration.screenshots.slice(1);
          const isSingle = extras.length === 1;
          return (
            <section className="max-w-7xl mx-auto px-4 py-16">
              <div className={isSingle ? 'max-w-4xl mx-auto' : 'grid md:grid-cols-2 gap-6'}>
                {extras.map((s, i) => (
                  <LandingFigure key={i} src={s.src} alt={s.alt} caption={s.caption} />
                ))}
              </div>
            </section>
          );
        })()}

        {/* FAQ */}
        {integration.faq?.length > 0 && (
          <section className="max-w-4xl mx-auto px-4 py-16">
            <div className="mb-10">
              <div className="text-xs uppercase tracking-wider text-amber-400 font-semibold mb-2">{t('integrations_page.faq_label')}</div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white">{t('integrations_page.faq_headline')}</h2>
            </div>
            <div className="space-y-3">
              {integration.faq.map((item, i) => (
                <details key={i} className="group bg-white/5 border border-gray-800 rounded-xl overflow-hidden hover:border-gray-700 transition-colors">
                  <summary className="cursor-pointer list-none flex items-start justify-between gap-4 p-5">
                    <h3 className="text-white font-semibold text-base sm:text-lg">{item.question}</h3>
                    <ChevronRight className="w-5 h-5 text-gray-500 group-open:rotate-90 transition-transform flex-shrink-0 mt-0.5" />
                  </summary>
                  <div className="px-5 pb-5 text-gray-400 leading-relaxed text-sm sm:text-base">
                    {item.answer}
                  </div>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{t('integrations_page.detail_cta.headline', { name: integration.name })}</h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">{t('integrations_page.detail_cta.body')}</p>
          <Link to="/register" className="bg-amber-500 text-black px-8 py-4 rounded-xl font-semibold hover:bg-amber-400 transition-colors inline-flex items-center gap-2">
            {t('integrations_page.cta_start_free')}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </section>
        </ScreenshotGallery>
      </main>

      <Footer />
    </div>
  );
};

export default IntegrationsPage;
